import { Card, CardHeader, CardBody, Text, Flex, Box, Avatar } from '@backstage/ui';
import { useEntity, useRelatedEntities } from '@backstage/plugin-catalog-react';
import { RELATION_HAS_MEMBER, Entity } from '@backstage/catalog-model';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { appTranslationRef } from '../../../../core/i18n';
import { RiGroupLine } from '@remixicon/react';
import { EmptyState, Tile } from '../../../../shared/components';
import styles from './profiles.module.css';

const MembersContent = ({
  entities,
  loading,
  error,
}: {
  entities: Entity[] | undefined;
  loading: boolean;
  error: Error | undefined;
}) => {
  const { t } = useTranslationRef(appTranslationRef);

  if (loading) {
    return (
      <Flex direction="column" gap="3">
        {[1, 2, 3].map(i => (
          <Box key={i} className={styles.skeleton} style={{ height: '48px' }} />
        ))}
      </Flex>
    );
  }
  if (error) {
    return (
      <Text variant="body-medium" color="secondary">
        {t('members.error')}
      </Text>
    );
  }
  if (!entities || entities.length === 0) {
    return <EmptyState title={t('members.empty')} />;
  }
  return (
    <Flex direction="column" gap="2">
      {entities.map(user => {
        const spec = user.spec as Record<string, unknown> | undefined;
        const profile = spec?.profile as Record<string, string> | undefined;
        const displayName = profile?.displayName || user.metadata.title || user.metadata.name;
        const email = profile?.email || undefined;
        const picture = profile?.picture || undefined;

        return (
          <Tile
            key={user.metadata.name}
            href={`/catalog/${user.metadata.namespace || 'default'}/user/${user.metadata.name}`}
            compact
            leading={
              <Avatar
                src={picture || ''}
                name={displayName}
                size="small"
              />
            }
            title={displayName}
            subtitle={email}
          />
        );
      })}
    </Flex>
  );
};

export const MembersListCard = () => {
  const { entity } = useEntity();
  const { t } = useTranslationRef(appTranslationRef);
  const { entities, loading, error } = useRelatedEntities(entity, {
    type: RELATION_HAS_MEMBER,
    kind: 'user',
  });

  return (
    <Card>
      <CardHeader>
        <Flex gap="3" align="center">
          <RiGroupLine size={18} className={styles.headerIcon} />
          <Text variant="title-small" weight="bold">
            {t('members.title')}
          </Text>
          {entities && entities.length > 0 && (
            <Text variant="body-x-small" className={styles.countBadge}>
              {entities.length}
            </Text>
          )}
        </Flex>
      </CardHeader>
      <CardBody>
        <MembersContent entities={entities} loading={loading} error={error} />
      </CardBody>
    </Card>
  );
};
